import {
	FIXEDCACHEKEYS,
	handleApiErrorWithNetworkFallback,
	HTTPSTATUSCODE,
	type IBusiness,
	type ILoginError,
	linkTargetQuery,
	type LoginSchemaType,
	type OTPTYPE,
} from 'BreetHelpers';
import { useLoginSuccess } from 'BreetHooks';
import { selectLinkActionTarget, setCredentials, useAppDispatch, useAppSelector, useLoginMutation } from 'BreetRedux';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useModalApi } from './useModalApi';

export const useLoginApi = () => {
	const navigate = useNavigate();

	const [login, { isLoading }] = useLoginMutation({ fixedCacheKey: FIXEDCACHEKEYS.LOGIN });

	const dispatch = useAppDispatch();

	// Preserve the sandbox/development env pin across the hop to verify-email (see linkTargetQuery).
	const linkTarget = useAppSelector(selectLinkActionTarget);

	const { closeModalDirect, openModalDirect } = useModalApi();

	const { onLoginSuccess } = useLoginSuccess();

	const handleLoginResponse = useCallback(
		(payload: LoginSchemaType & { businessId?: string }, statusCode?: number, otpType?: OTPTYPE, business?: IBusiness) => {
			const is2faRequired = statusCode === HTTPSTATUSCODE.TWOFA;
			const isNewDevice = statusCode === HTTPSTATUSCODE.NEWDEVICE;

			if (is2faRequired || isNewDevice) {
				dispatch(
					setCredentials({
						email: payload.email,
						password: payload.password,
						businessId: payload.businessId,
						otpType,
					})
				);
				navigate(otpType !== 'authenticator' && isNewDevice ? '/new-device' : '/2fa')?.catch(console.error);
				return;
			}

			onLoginSuccess(business, payload.email).catch(console.error);
		},
		[dispatch, navigate, onLoginSuccess]
	);

	const loginUser = useCallback(
		(payload: LoginSchemaType & { businessId?: string; onFinalCallback?: () => void }) => {
			const { onFinalCallback, ...rest } = payload;

			openModalDirect({ id: 'loadingDialog', dialogMessage: 'Authenticating User' });

			login(rest)
				.unwrap()
				.then((data) => {
					handleLoginResponse(rest, data.statusCode, data.data?.otpCodeType, data.data?.business);
				})
				.catch((e: unknown) => {
					const errResponse = e as ILoginError;
					const isMultipleTeams = errResponse.status === HTTPSTATUSCODE.MULTIPLETEAMS;
					const isTeamEnvironts = errResponse.status === HTTPSTATUSCODE.TRANSACTIONNOAUTH;

					if (isMultipleTeams) {
						dispatch(
							setCredentials({
								email: payload.email,
								password: payload.password,
								businessId: payload.businessId,
							})
						);

						openModalDirect({ id: 'switchUserAccount' });
						return;
					}

					if (isTeamEnvironts && errResponse.data.meta.token) {
						navigate(
							`/verify-email/?token=${errResponse.data.meta.token}&email=${payload.email}${linkTargetQuery(linkTarget)}`
						)?.catch(console.error);
					}

					handleApiErrorWithNetworkFallback(e);
				})
				.finally(() => {
					closeModalDirect('loadingDialog');
					onFinalCallback?.();
				});
		},
		[closeModalDirect, dispatch, handleLoginResponse, login, navigate, openModalDirect, linkTarget]
	);

	return {
		loginUser,
		isLoading,
		handleLoginResponse,
	};
};
